import type { CascadeOption, CascadeValue } from '@byecode/ui'
import { CascadeList, Checkbox, Empty, Flex, getLeafNodes, IconFont, MobileModal, Text } from '@byecode/ui'
import type { DrawerProps } from '@mantine/core'
import { reduce } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'

import { useLanguageContext } from '../../../contexts'
import * as SC from './styles'

interface CascadeDrawerProps {
    opened: boolean
    isMultiple?: boolean
    leftIcon?: string
    options?: CascadeOption[]
    showFooterBtn?: boolean
    value: CascadeValue
    title: string
    selectionMode: 'any' | 'auto-leaf' | 'leaf-only'
    showPath?: boolean
    path?: string
    target: DrawerProps['target']
    allSelected?: boolean
    style?: React.CSSProperties
    onPathChange?: (v: CascadeOption) => void
    onClose: () => void
    onFinish?: (v: CascadeValue) => void
}

export const CascadeDrawer: React.FC<CascadeDrawerProps> = ({
    opened,
    leftIcon,
    isMultiple,
    showFooterBtn,
    options,
    title,
    value,
    showPath,
    allSelected,
    target,
    selectionMode,
    style,
    onClose,
    onPathChange,
    onFinish
}) => {
    const [selectValue, setSelectValue] = useState(value)
    const [selectOptions, setSelectOptions] = useState(options ?? [])
    const [keywords, setKeywords] = useState('')
    const [id, setId] = useState('')
    const { convertTextByLanguage } = useLanguageContext()

    useUpdateEffect(() => {
        setSelectValue(value)
        value.length === 0 && setId('')
    }, [value])

    useUpdateEffect(() => {
        setSelectOptions(options ?? [])
    }, [options])

    const flatOptions = useMemo(() => {
        function getChildOption(option: CascadeOption, list: CascadeOption[]) {
            const { value, label, path, labelPath, children = [] } = option
            let arrList = list
            for (const child of children) {
                const newChild = {
                    ...child,
                    path: `${path}/${child.value}`,
                    labelPath: `${labelPath}/${child.label}`,
                    isLast: (child.children ?? []).length === 0
                }
                arrList = [...arrList, newChild, ...getChildOption(newChild, [])]
            }
            return arrList
        }
        return (
            options?.reduce<CascadeOption[]>((pre, cur) => {
                const newOption = { ...cur, path: cur.value, labelPath: cur.label, isLast: (cur.children ?? []).length === 0 }
                return [...pre, newOption, ...getChildOption(newOption, [])]
            }, []) ?? []
        )
    }, [options])

    const filterOptions = useMemo(() => {
        switch (selectionMode) {
            case 'any': {
                const newOptions = flatOptions.filter(option => option.label?.includes(keywords) || option.labelPath?.includes(keywords))
                return keywords === '' ? selectOptions : newOptions
            }
            case 'auto-leaf':
            case 'leaf-only': {
                const newOptions = flatOptions.filter(
                    option => (option.label?.includes(keywords) || option?.labelPath?.includes(keywords)) && option.isLast
                )
                return keywords === '' ? selectOptions : newOptions
            }
            default: {
                return selectOptions
            }
        }
    }, [flatOptions, keywords, selectOptions, selectionMode])

    const idOptionPath = useMemo(() => flatOptions.find(v => v.value === id)?.labelPath ?? '', [flatOptions, id])

    const tagList = useMemo(() => {
        if (selectionMode === 'leaf-only') {
            return reduce<string, CascadeOption[]>(
                (p, v) => {
                    const item = flatOptions.find(d => d.value === v)
                    return item ? [...p, item] : p
                },
                [],
                selectValue
            )
        }
        const floatTagList = flatOptions.map(v => ({ ...v, childLeafNodes: getLeafNodes(v) }))
        // 所有含有全部树叶节点的父级节点
        const nodeChildMap: Record<string, string[]> = {}
        floatTagList.forEach(v => {
            if (
                v.childLeafNodes?.every(d => selectValue.includes(d.value)) &&
                v.childLeafNodes.length > 0
                // (v.isLast && selectValue.includes(v.value))
            ) {
                nodeChildMap[v.value] = v.childLeafNodes?.map(v => v.value) ?? []
            }
        })
        const topLevelNodeIds = selectValue.filter(v => options?.some(d => d.value === v))
        // 不含子节点的一级节点
        const tagMapList = Object.entries(nodeChildMap)
        const nodeIds = new Set<string>([...tagMapList.map(([key, v]) => key), ...topLevelNodeIds])
        for (const [key, ids] of tagMapList) {
            for (const [key2, ids2] of tagMapList) {
                // 如果key的ids被ids2包含，则删除key，并且添加key2
                if (ids2.every(v => ids.includes(v)) && key !== key2) {
                    nodeIds.delete(key2)
                    nodeIds.add(key)
                }
            }
        }
        return reduce<string, CascadeOption[]>(
            (p, v) => {
                const item = flatOptions.find(d => d.value === v)
                return item ? [...p, item] : p
            },
            [],
            [...nodeIds]
        )
    }, [flatOptions, options, selectValue, selectionMode])

    const handleClose = useCallback(() => {
        onClose()
        setKeywords('')
        setId('')
        setSelectOptions(options ?? [])
    }, [onClose, options])

    const handleFinish = useCallback(
        (v: CascadeValue) => {
            const newValue = v.filter(v => flatOptions.some(d => d.value === v && d.isLast))
            onFinish?.(newValue)
            handleClose()
        },
        [flatOptions, handleClose, onFinish]
    )

    const handleReset = useCallback(() => {
        handleFinish?.([])
        setKeywords('')
        setSelectOptions(options ?? [])
        setSelectValue([])
    }, [handleFinish, options])

    const handleBack = useCallback(
        (i: number) => {
            const path = flatOptions.find(v => v.value === id)?.path ?? ''
            const parentPath = path.split('/').slice(0, i).join('/')
            const parentOption = flatOptions.find(option => option.path === parentPath)
            setSelectOptions(parentOption?.children ?? options ?? [])
            setId(parentOption?.value ?? '')
        },
        [flatOptions, options, id]
    )

    const handleChange = useCallback(
        (v: CascadeValue) => {
            setSelectValue(v)
            if (isMultiple) {
                return
            }
            !showFooterBtn && onFinish?.(v)
        },
        [isMultiple, onFinish, showFooterBtn]
    )

    const handleSearchValueChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
        const word = ev.target.value.trim()
        setKeywords(word)
    }, [])

    const handlePathChange = useCallback(
        (option: CascadeOption) => {
            const pathOption = flatOptions.find(v => v.value === option.value)
            if (pathOption) {
                const children = pathOption.children ?? []
                onPathChange?.(pathOption)
                if (children.length > 0 && !keywords) {
                    setId(option.value)
                    setSelectOptions(children)
                }
                if (!showFooterBtn) {
                    children.length === 0 && !isMultiple && handleClose()
                }
            }
        },
        [flatOptions, handleClose, isMultiple, keywords, onPathChange, showFooterBtn]
    )

    return (
        <MobileModal
            data-ignore-click-away
            target={target}
            title={title}
            open={opened}
            closeOnClickOverlay
            style={style}
            styles={{
                modal: {
                    height: '70%'
                },
                body: {
                    backgroundColor: 'var(--color-gray-50)',
                    overflow: 'hidden'
                },
                close: {
                    display: 'none'
                },
                actions: {
                    marginLeft: 'unset!important'
                }
            }}
            onClose={handleClose}
            withCloseIcon={false}
            leftSlot={
                <SC.FinishButton color="var(--color-app-main)" onClick={handleReset}>
                    {convertTextByLanguage('clear')}
                </SC.FinishButton>
            }
            extra={
                <SC.FinishButton role="button" onClick={() => handleFinish(selectValue)}>
                    {convertTextByLanguage('complete')}
                </SC.FinishButton>
            }
        >
            <SC.Container>
                <SC.Cursor>
                    <SC.SearchInput
                        value={keywords}
                        placeholder={convertTextByLanguage('search')}
                        onChange={handleSearchValueChange}
                        prefix={<IconFont type="SearchLine" color="var(--color-gray-400)" size={16} />}
                        suffix={
                            keywords && (
                                <IconFont
                                    type="CloseCircle"
                                    color="var(--color-gray-400)"
                                    size={16}
                                    onClick={() => {
                                        setKeywords('')
                                        setSelectOptions(options ?? [])
                                    }}
                                />
                            )
                        }
                    />
                </SC.Cursor>

                <SC.TagListContainer>
                    {tagList?.map(tag => (
                        <SC.Tag key={tag.value}>
                            {showPath ? tag.labelPath : tag.label}
                            <IconFont
                                style={{ marginLeft: 2, cursor: 'pointer' }}
                                color="var(--color-gray-400)"
                                type="Close"
                                onClick={() => {
                                    const leafNodeIds = new Set(getLeafNodes(tag).map(v => v.value))
                                    const newValue = tag.isLast
                                        ? selectValue.filter(v => v !== tag.value)
                                        : selectValue.filter(v => !leafNodeIds.has(v))
                                    setSelectValue(newValue)
                                }}
                            />
                        </SC.Tag>
                    ))}
                </SC.TagListContainer>

                {idOptionPath && (
                    <SC.Path onClick={() => handleBack(idOptionPath.split('/').length - 1)}>
                        <IconFont type="ArrowLeftSmall" color="var(--color-gray-400)" size={14} />
                        {idOptionPath.split('/').length > 0 && (
                            <Text size={14} color="var(--color-gray-400)">
                                {idOptionPath}
                            </Text>
                        )}
                    </SC.Path>
                )}
                {selectOptions.length === 0 ? (
                    <Empty style={{ height: '100%' }} description={convertTextByLanguage('noFindData')} icon="Nodata-8i554976" />
                ) : (
                    <Flex
                        direction="column"
                        style={{
                            flex: 1,
                            overflow: 'hidden',
                            gap: 12
                        }}
                    >
                        <Flex style={{ flex: 1, overflow: 'hidden' }}>
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch'
                                }}
                            >
                                <CascadeList
                                    data={selectValue}
                                    selectionMode={selectionMode}
                                    options={filterOptions}
                                    allSelected={allSelected && !id && keywords === ''}
                                    multiple={isMultiple}
                                    columnWidth="100%"
                                    styles={{
                                        root: {
                                            overflow: 'hidden',
                                            width: '100%'
                                            // height: '100%'
                                        },
                                        columns: {
                                            border: '1px solid var(--color-gray-200)',
                                            borderRadius: 8,
                                            maxHeight: '100%',
                                            flex: 'none',
                                            backgroundColor: 'var(--color-white)',
                                            overflow: 'hidden'
                                        },
                                        column: {
                                            borderStyle: 'none',
                                            flex: 'none',
                                            padding: 0
                                        },
                                        content: {
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8
                                        },
                                        list: {
                                            height: 'auto',
                                            maxHeight: '100%'
                                        },
                                        item: {
                                            height: '48px!important',
                                            backgroundColor: 'var(--color-white)',
                                            borderBottom: '1px solid var(--color-gray-200)',
                                            overflow: 'hidden',
                                            '&:last-child': {
                                                border: 'none'
                                            }
                                        },
                                        activeIcon: {
                                            color: 'var(----color-app-main)'
                                        }
                                    }}
                                    emptyProps={{
                                        style: {
                                            padding: '40px 0'
                                        }
                                    }}
                                    onChange={handleChange}
                                    onPathChange={handlePathChange}
                                    itemChildrenComponent={column => {
                                        const leafNodes = column.children && column.children.length > 0 ? getLeafNodes(column) : [column]
                                        const leafValues = leafNodes.map(node => node.value)
                                        const allSelected = leafValues.every(val => selectValue.includes(val))
                                        const partiallySelected = !allSelected && leafValues.some(val => selectValue.includes(val))
                                        const checked = selectionMode === 'any' ? selectValue.includes(column.value) : allSelected
                                        return (
                                            <>
                                                <Flex alignItems="center" gap={8} style={{ flex: 1, overflow: 'hidden' }}>
                                                    <div style={{ width: 16 }}>
                                                        {checked && (
                                                            <IconFont type="Tick" size={16} color="var(--byecode-colors-colorPrimary)" />
                                                        )}
                                                        {!allSelected && partiallySelected && selectionMode === 'auto-leaf' && (
                                                            <IconFont type="Reduce" size={16} color="var(--byecode-colors-colorPrimary)" />
                                                        )}
                                                    </div>
                                                    <Text
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                keywords === ''
                                                                    ? column.label
                                                                    : column.labelPath?.replaceAll(
                                                                          keywords,
                                                                          `<span style="color: var(--color-main);">${keywords}</span>`
                                                                      ) ?? ''
                                                        }}
                                                    />
                                                </Flex>
                                                <Flex alignItems="center" gap={4}>
                                                    {(column.children ?? []).length > 0 && !keywords && (
                                                        <IconFont
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handlePathChange(column)
                                                            }}
                                                            type="ArrowRightSmall"
                                                            size={16}
                                                            color="var(--color-gray-400)"
                                                        />
                                                    )}
                                                </Flex>
                                            </>
                                        )
                                    }}
                                />
                            </div>
                        </Flex>
                    </Flex>
                )}
            </SC.Container>
        </MobileModal>
    )
}
